import React, { Fragment, useContext, useEffect, useReducer, useState } from 'react';
import Screen from 'utils/helpers/ScreenSize';
import { Dialog, Transition } from '@headlessui/react';
import { StoreContext } from 'pages/_app';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { MapIcon, ArrowRightIcon } from 'assets/img';
import classNames from 'classnames';
import { formattedAddres, parsedAddress } from 'utils/helpers/addresses';
import AddressPicker from 'components/AddressPicker';
import moment from 'moment-timezone';
import QueryString from 'qs';

function AddressPickerModal({ open, setOpenSavedAddressModal, setModal }) {
  const store = useContext(StoreContext);
  const userStore = toJS(store?.userStore);
  const router = useRouter();

  const { locationAddresses, activeEmployees, selectedDate, selectedTime } = userStore || {};

  const handleBack = () => {
    setOpenSavedAddressModal(false);
  };

  const buildFullAddress = attributes => {
    if (!attributes) return '';
    const suitePart = attributes?.suite ? `${attributes.suite} - ` : '';
    const streetPart = `${attributes?.street_number ?? ''} ${attributes?.street_name ?? ''}`.trim();
    const cityProvPostal = `${attributes?.city ?? ''}, ${attributes?.province ?? ''} ${attributes?.postal_code ?? ''}`.trim();
    return `${suitePart}${streetPart}, ${cityProvPostal}`.replace(/\s+,/g, ',').replace(/,\s*,/g, ', ');
  };

  const setInvoiceDetailsForLocation = locationId => {
    const account = activeEmployees?.find(
      emp => emp?.attributes?.organization_id == locationId
    );
    const isInvoiceEnabled = account?.attributes?.invoice_payments == 'enabled';

    if (isInvoiceEnabled)
      store.userStore.setInvoiceDetails({
        invoice_payments: account?.attributes?.invoice_payments,
        account_billing_address: account?.attributes?.account_billing_address,
        account_billing_emails: account?.attributes?.account_billing_emails,
        account_billing_name: account?.attributes?.account_billing_name,
      });
    else
      store.userStore.setInvoiceDetails({
        invoice_payments: '',
        account_billing_address: '',
        account_billing_emails: '',
        account_billing_name: '',
      });
  };

  const closeAllModals = () => {
    if (setModal && setOpenSavedAddressModal) {
      setModal(false);
      setOpenSavedAddressModal(false);
      setTimeout(() => {
        document.documentElement.removeAttribute('style');
      }, 0);
    }
  };

  const syncUrlAndDates = label => {
    const date = moment(selectedDate, 'YYYY-MM-DD');
    const dateValue = date.isValid() ? date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

    router.replace(
      {
        pathname: router?.pathname,
        query: {
          ...QueryString.parse(router?.query),
          address: label,
          date: dateValue,
          time: selectedTime,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );

    store?.userStore?.setLoader(true);
    store.userStore
      .getAvailableDates({
        address: label,
        month: moment().month() + 1,
        year: moment().year(),
        current_date_time: moment().format('YYYY-MM-DD HH:mm'),
      })
      .then(() => {
        store?.userStore?.setLoader(false);
      });
  };

  const selectAddress = addressObj => {
    if (!addressObj) return;

    const label = formattedAddres(addressObj?.attributes);

    store.userStore?.setAddressID(addressObj?.id);
    store.userStore?.setAddressLocationID(addressObj?.locationId);
    store.userStore?.setAddress(label, parsedAddress(addressObj?.attributes));

    setInvoiceDetailsForLocation(addressObj?.locationId);
    syncUrlAndDates(label);
    closeAllModals();
  };

  const AddressItem = ({ addressObj, onSelect }) => {
    const nickname = addressObj?.attributes?.nickname || 'Address';
    const destinationCode = addressObj?.attributes?.destination_code;
    return (
      <button
        type="button"
        className="flex items-center justify-between w-full py-4"
        onClick={() => onSelect(addressObj)}
      >
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-6 h-6">
            <Image src={MapIcon} alt="Map Icon" width={20} height={20} />
          </span>
          <div className="flex flex-col items-start text-left">
            <span className="text-[var(--Text-Paragraph,#2D3958)] font-inter text-[16px] font-normal leading-[24px]">{nickname}{destinationCode ? ` (${destinationCode})` : ''}</span>
            <span className="text-[var(--Text-Secondary-text,#566985)] font-inter text-[14px] font-normal leading-[21px]">
              {buildFullAddress(addressObj?.attributes)}
            </span>
          </div>
        </div>
        <Image src={ArrowRightIcon} alt="Select" width={16} height={16} />
      </button>
    );
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpenSavedAddressModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed top-0 right-0 left-0 sm:inset-0 overflow-y-auto">
            <div className="flex min-h-screen sm:min-h-full sm:items-center sm:justify-center sm:p-4 md:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                
                <Dialog.Panel className="flex flex-col w-full transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all h-screen min-h-screen px-4 pt-4 pb-16 sm:h-auto sm:min-h-0 sm:w-[480px] sm:rounded-2xl sm:py-10 sm:px-8">
                  {/* Mobile layout */}
                  <div className="block sm:hidden flex flex-col h-full w-full">
                    {/* Header */}
                    <div className="flex items-center gap-2 h-11 self-stretch">
                      <button type="button" className="flex items-center gap-2" onClick={handleBack}>
                        <Image src={ArrowRightIcon} alt="Back" width={16} height={16} className="rotate-180" />
                        <span className="text-[var(--Primary-MP,#575BE8)] font-inter text-[16px] font-normal leading-[24px]">Back</span>
                      </button>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col items-start gap-6 self-stretch mt-2">
                      <Dialog.Title as="h3" className="text-[var(--plain,#2D3958)] font-inter font-semibold text-[26px] leading-[39px]">
                        Addresses
                      </Dialog.Title>

                      <div className="flex flex-col items-start gap-2 self-stretch">
                        <span className="text-[var(--plain,#2D3958)] font-inter text-[22px] font-medium leading-[33px]">
                          Saved Addresses
                        </span>

                        <div className="flex flex-col self-stretch divide-y" style={{ borderColor: '#DEE2E6' }}>
                          {(locationAddresses || []).map(addr => (
                            <AddressItem key={addr?.id} addressObj={addr} onSelect={selectAddress} />
                          ))}
                          {(!locationAddresses || locationAddresses.length === 0) && (
                            <div className="py-6 text-sm text-[#566985]">No saved addresses found.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop/Tablet layout  */}
                  <div className="hidden sm:flex flex-col flex-1 md:flex-initial items-end gap-8">
                    <div className="flex w-full items-center">
                      <Dialog.Title as="h3" className="font-inter-semibold text-[24px] leading-[36px] text-[#2D3958]">
                        Address
                      </Dialog.Title>
                    </div>
                    <div className="flex flex-col justify-center items-start w-full">
                      <span className="text-[#3A3A3A] text-[16px] font-inter font-medium leading-[24px]">Saved Addresses</span>
                      <div className="flex flex-col items-start gap-1 w-full self-stretch">
                        <div className="autocomplete-container flex justify-between items-center self-stretch h-[48px] rounded-lg bg-white border border-gray-300 px-3">
                          <div className="flex items-center justify-center w-6 h-6 shrink-0">
                            <Image src={MapIcon} alt="Map Icon for Address" width={16} height={16} />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <AddressPicker fromModal={true} backgroundColor={true} setAddress={() => {}} setModal={setModal} setOpenSavedAddressModal={setOpenSavedAddressModal} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>

              </Transition.Child>
            </div>
          </div>
      </Dialog>
    </Transition>
  );
}

export default observer(AddressPickerModal);